import { useState } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SegmentedControl } from '@/components/ui/segmented-control'
import type { TrendMonth } from '@/models/salesAnalytics'
import { SERIES_COLORS } from '../AgentReporting/chartPalette'

export interface TrendMetric {
  key: Exclude<keyof TrendMonth, 'month'>
  label: string
  ratio?: boolean
  format?: (value: number) => string
}

export const monthLabel = (month: string) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })

const plain = (value: number) => value.toLocaleString('en-US')

/** One metric at a time for whatever the page's filters select, on a single axis. */
export function MonthlyTrendCard({
  title,
  description,
  months,
  metrics,
  seriesLabel,
  children,
}: {
  title: string
  description: string
  months: TrendMonth[]
  metrics: TrendMetric[]
  /** What the one line counts, e.g. "Inbound leads"; the source filter decides it. */
  seriesLabel: string
  children?: React.ReactNode
}) {
  const [selected, setSelected] = useState<string>(metrics[0].key)
  const metric = metrics.find((m) => m.key === selected) ?? metrics[0]
  const format = metric.format ?? plain
  const data = months.map((month) => ({ month: month.month, value: month[metric.key] }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="text-sm text-neutral-500">{description}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <SegmentedControl
          value={metric.key}
          onValueChange={setSelected}
          options={metrics.map((m) => ({ value: m.key, label: m.label }))}
        />
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#E4E3E0" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              tickFormatter={monthLabel}
              tick={{ fill: '#666764', fontSize: 12 }}
              stroke="#D6D5D2"
            />
            <YAxis
              tickFormatter={format}
              tick={{ fill: '#666764', fontSize: 12 }}
              stroke="#D6D5D2"
              width={56}
              allowDecimals={metric.ratio === true}
            />
            <Tooltip
              labelFormatter={(value) => monthLabel(String(value))}
              formatter={(_value, _name, item) => {
                // Read the datum: a month with nothing to divide by is a gap, not a zero.
                const raw = (item as { payload?: { value: number | null } }).payload?.value
                return [raw == null ? 'no data' : format(raw), `${seriesLabel} · ${metric.label}`]
              }}
              contentStyle={{ borderRadius: 8, borderColor: '#E4E3E0', fontSize: 12 }}
            />
            <Line
              type="linear"
              dataKey="value"
              name={seriesLabel}
              stroke={SERIES_COLORS[0]}
              strokeWidth={2}
              connectNulls={false}
              dot={{ r: 3 }}
              activeDot={{ r: 5, strokeWidth: 2, stroke: '#ffffff' }}
            />
          </LineChart>
        </ResponsiveContainer>
        {children}
      </CardContent>
    </Card>
  )
}
